import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import AnimatedSection from './AnimatedSection';

// Import Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import './ShortVideoSection.css'; // Custom styles

const getEmbedUrl = (url) => {
    if (!url) return null;

    // YouTube Shorts
    if (url.includes('youtube.com/shorts/')) {
        const videoId = url.split('/shorts/')[1].split('?')[0];
        return { type: 'youtube', src: `https://www.youtube.com/embed/${videoId}` };
    }
    // YouTube Regular (fallback)
    else if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId;
        if (url.includes('youtu.be')) {
            videoId = url.split('/').pop();
        } else {
            const urlObj = new URL(url);
            videoId = urlObj.searchParams.get('v');
        }
        return { type: 'youtube', src: `https://www.youtube.com/embed/${videoId}` };
    }
    // TikTok
    else if (url.includes('tiktok.com')) {
        // TikTok embeds are trickier without an API. 
        // We will try using the TikTok Embed link if the user provides the video ID, 
        // but often users paste the full URL.
        // For simplicity and reliability without an API key, we might use the standard TikTok embed iframe if we can extract the video ID.
        // A common reliable way for a React app without backend scraping is asking users to input the "Embed Code" or just properly sanitizing the ID.
        // However, let's try to extract the ID.
        const parts = url.split('/video/');
        if (parts.length > 1) {
            const videoId = parts[1].split('?')[0];
            return { type: 'tiktok', src: `https://www.tiktok.com/embed/v2/${videoId}` };
        }
        // Fallback: If user pasted a full long URL maybe we return null or try generic
        return { type: 'tiktok', id: url }; // Just return URL, component handles it? No, iframe needs ID.
    }

    return null;
};

const ShortVideoItem = ({ video, embedData }) => {
    const [isInteracting, setIsInteracting] = useState(false);

    return (
        <div
            className={`short-video-card ${isInteracting ? 'interacting' : ''}`}
            onClick={() => setIsInteracting(true)}
            onMouseLeave={() => setIsInteracting(false)}
        >
            <div className="short-video-wrapper">
                <div className="interaction-overlay" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 2,
                    display: isInteracting ? 'none' : 'block',
                    cursor: 'pointer'
                }}></div>
                {embedData.type === 'youtube' ? (
                    <iframe
                        src={embedData.src}
                        title={video.title || "YouTube Short"}
                        frameBorder="0"
                        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                        style={{ pointerEvents: isInteracting ? 'auto' : 'none' }}
                    ></iframe>
                ) : embedData.type === 'tiktok' ? (
                    <iframe
                        src={embedData.src}
                        title={video.title || "TikTok Video"}
                        allowFullScreen
                        scrolling="no"
                        frameBorder="0"
                        allow="encrypted-media; picture-in-picture; accelerometer 'none'; gyroscope 'none'"
                        style={{ pointerEvents: isInteracting ? 'auto' : 'none' }}
                    ></iframe>
                ) : null}
            </div>
        </div>
    );
};

const ShortVideoSection = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                const q = query(
                    collection(db, 'shortVideos'),
                    orderBy('date', 'desc'),
                    limit(10)
                );
                const snapshot = await getDocs(q);
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setVideos(data);
            } catch (error) {
                console.error("Error fetching short videos:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchVideos();
    }, []);

    if (loading || videos.length === 0) return null;

    return (
        <AnimatedSection>
            <section className="section short-video-section">
                <div className="container">
                    <h2 style={{ color: 'inherit' }}>Video Shorts & TikTok</h2>
                    <Swiper
                        modules={[Navigation, Pagination, A11y]}
                        spaceBetween={20}
                        slidesPerView={1}
                        navigation
                        pagination={{ clickable: true }}
                        loop={videos.length >= 8}
                        breakpoints={{
                            320: {
                                slidesPerView: 1,
                                spaceBetween: 20,
                            },
                            480: {
                                slidesPerView: 2,
                                spaceBetween: 20,
                            },
                            768: {
                                slidesPerView: 3,
                                spaceBetween: 15,
                            },
                            1024: {
                                slidesPerView: 4,
                                spaceBetween: 20,
                            },
                        }}
                        className="shorts-swiper"
                    >
                        {videos.map(video => {
                            const embedData = getEmbedUrl(video.url);
                            if (!embedData) return null;

                            return (
                                <SwiperSlide key={video.id}>
                                    <ShortVideoItem video={video} embedData={embedData} />
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
            </section>
        </AnimatedSection>
    );
};


export default ShortVideoSection;
